<template>
  <section class="main">
    <loader v-if="!loaded" />
    <aside v-if="loaded" class="sidebar">
      <div class="sidebar__my">
        <div class="header">
          <template v-if="isDeviceLinked">
            <span class="header__active">{{ this.$t("ACTIVE_DEVICE") }}</span>
            <img class="header__img" :src="currentDevice.icon" />
            <div class="header__desc">
              <span class="header__title">{{ currentDevice.name }}</span>
              <span class="header__device">{{ currentDevice.title }})</span>
              <ul class="header__list">
                <li
                  class="header__status header__status_jail"
                  :class="{
                    'header__status_jail-active':
                      currentDevice.jailbreak === 'JAILBROKEN' &&
                      currentDevice.jailbreakPrefix !== '? ',
                  }"
                >
                  {{
                    currentDevice.jailbreakPrefix + $t(currentDevice.jailbreak)
                  }}
                </li>
                <template v-if="currentDevice.pro">
                  <li class="header__divider"></li>
                  <li class="header__status header__status_pro">Pro</li>
                </template>
                <template v-if="currentDevice.plus">
                  <li class="header__divider"></li>
                  <li class="header__status header__status_pro">Plus</li>
                </template>
              </ul>
            </div>
          </template>

          <template v-else>
            <img class="header__img" src="@/assets/icon-nodevice.svg" />
            <div class="header__desc">
              <span class="header__error-status g-status g-status_error">{{
                $t("NO_DEVICE_LINKED")
              }}</span>
              <router-link to="/link" class="g-button">{{
                $t("LINK_YOUR_DEVICE")
              }}</router-link>
            </div>
          </template>
        </div>
        <nav>
          <ul class="nav" :class="{ nav_disabled: !isDeviceLinked }">
            <li class="nav__item" v-for="item in nav" :key="item.title">
              <a v-if="item.external" class="nav__link" :href="item.link" :target="item.target">
                {{ item.title }}
              </a>
              <router-link v-else class="nav__link" :to="'/my' + item.link">{{
                item.title
              }}</router-link>
            </li>
          </ul>
        </nav>
      </div>
    </aside>
    <slot v-if="loaded">
      <router-view />
    </slot>
  </section>
</template>

<script>
import Loader from '@/components/Loader';

export default {
  components: { Loader },
  computed: {
    nav() {
      return [
        {
          title: this.$t('MY_DEVICES'),
          link: '/devices',
        },
        {
          title: `${this.$t('INSTALL CUSTOM APPLICATION')} (MyAppStore)`,
          link: '/store',
        },
        {
          title: this.$t('MY_DYLIBS'),
          link: '/dylibs',
        },
        {
          title: this.$t('MY_ALTSTORE_REPOSITORIES'),
          link: '/repos',
        },
        {
          title: this.$t('CHECK FOR UPDATES'),
          link: '/updates',
        },
        {
          title: this.$t('CONFIGURE_FEATURES'),
          link: '/configure',
        },
        {
          title: this.$t('VIEW_PLUS_STATUS'),
          link: '/plus',
        },
        {
          title: this.$t('P2P_SIGNING_MARKETPLACE'),
          link: '/p2p-certificates',
        },
        {
          title: this.$t('VIEW_PRO_STATUS'),
          link: '/pro',
        },
        {
          title: this.$t('VIEW DEVICE STATUS'),
          link: '/status',
        },
        {
          title: this.$t('VIEW_IPA_CACHE_STATUS'),
          link: '/ipa-cache',
        },
        {
          title: this.$t('UNLINK THIS DEVICE'),
          link: '/unlink',
        },
        {
          title: this.$t('TROUBLESHOOTING'),
          link: 'https://telegra.ph/App-installation-troubleshooting-03-20',
          external: true,
          target: '_blank',
        },
      ];
    },
    loaded() {
      const { configLoaded, devicesLoaded } = this.$store.getters;
      return configLoaded && devicesLoaded;
    },
    isDeviceLinked() {
      return this.$store.getters.isDeviceLinked;
    },
    currentDevice() {
      return this.$store.getters.devices
        .find(device => device.lt === this.$store.getters.lt) || {};
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: flex;
}

.main::v-deep .intro {
  background: var(--my-heading-bg);
  border-radius: 2px;
  font-size: 16px;
  font-weight: 600;
  padding: 10px 15px;
  display: block;
  margin-bottom: 25px;

  @include notebook {
    font-size: 14px;
    font-weight: 400;
    line-height: 16px;
    padding: 7px 10px;
    margin-bottom: 15px;
  }
}

.sidebar {
  flex: 0 0 400px;
  margin-right: 30px;

  @include notebook {
    display: none;
  }

  &__my {
    background: var(--bg);
    border: 1px solid var(--tertiary-accent);
    box-shadow: 0 4px 10px 0 var(--shadow-color-1);
    border-radius: 8px;
    overflow: hidden;
  }
}

.header {
  padding: 20px 25px;
  display: flex;
  align-items: center;
  position: relative;

  &__error-status {
    font-size: 14px;
    margin-bottom: 10px;
  }

  &__title {
    color: var(--text-primary);
    font-weight: 500;
    display: block;
    margin-bottom: 10px;
  }

  &__active {
    background: var(--tertiary-accent);
    border-radius: 2px;
    padding: 4px;
    color: var(--text-tertiary-accent);
    font-size: 10px;
    position: absolute;
    right: 0;
    top: 0;
  }

  &__img {
    height: 90px;
    margin-right: 15px;
    filter: var(--img-filter);
  }

  &__list {
    display: flex;
    margin-top: 8px;
  }

  &__status {
    border-radius: 2.5px;
    display: flex;
    align-items: center;
    padding: 3px;

    &_jail {
      font-size: 12px;
      background: var(--tertiary-accent);
      color: var(--text-tertiary-accent);

      &-active {
        color: #053305;
        background: var(--bg-success);
      }
    }

    &_pro {
      text-transform: uppercase;
      background: $primary;
      font-size: 11px;
      font-weight: 700;
      color: #fff;
      padding-bottom: 2px;
    }
  }

  &__divider {
    background: #d8d8d8;
    width: 5px;
    height: 5px;
    border-radius: 100%;
    margin: auto 5px;
  }
}

.nav__link,
.links__link {
  display: block;
  padding: 10px 15px;
  color: var(--text-primary);
}

.nav,
.links {
  &_disabled {
    opacity: 0.5;
    pointer-events: none;
  }
}

.nav {
  &__link {
    border-top: 1px solid var(--tertiary-accent);

    &.router-link-active {
      background-image: var(--pink-gradient);
    }
  }
}

.links {
  &__link {
    padding-top: 15px;
    padding-bottom: 15px;
    border-bottom: 1px solid #e5e5e5;
  }
}
</style>
